"use client";

import { useEffect, useEffectEvent, useRef, useState } from "react";
import type { ImageJob, ImageOutput } from "@/lib/image/job";
import { runImageJob } from "@/workers/image-client";

type Request = { key: string; file: File; job: ImageJob };
type Done = { key: string; output?: ImageOutput; error?: string };

const ids = new WeakMap<File, number>();
let nextId = 1;
function fileId(file: File) {
  if (!ids.has(file)) ids.set(file, nextId++);
  return ids.get(file);
}

/** Live preview for the Adjust step: runs `job` on `file` in the worker,
 *  350 ms after the settings stop changing, keeping only the latest request. */
export function useImagePreview(file: File | undefined, job: ImageJob | null) {
  const key = file && job ? `${fileId(file)}|${JSON.stringify(job)}` : null;
  const [done, setDone] = useState<Done | null>(null);
  const queue = useRef<{ running: boolean; next: Request | null }>({
    running: false,
    next: null,
  });

  const run = useEffectEvent(async (req: Request) => {
    const q = queue.current;
    if (q.running) {
      q.next = req;
      return;
    }
    q.running = true;
    try {
      const output = await runImageJob(req.file, req.job);
      setDone({ key: req.key, output });
    } catch (e) {
      setDone({ key: req.key, error: (e as Error).message });
    } finally {
      q.running = false;
      const next = q.next;
      q.next = null;
      if (next) run(next);
    }
  });

  useEffect(() => {
    if (!key || !file || !job) return;
    const timer = setTimeout(() => run({ key, file, job }), 350);
    return () => clearTimeout(timer);
    // `key` already captures every change to file and job.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const current = done?.key === key ? done : null;
  const sameFile = !!key && done?.key.split("|")[0] === key.split("|")[0];
  return {
    /** The latest finished preview of this file (may be for older settings while busy). */
    output: sameFile ? done?.output : undefined,
    error: current?.error,
    busy: key !== null && !current,
  };
}
